import React, { Fragment, useState } from 'react';
import { GatsbyImage } from 'gatsby-plugin-image';
import { Dialog, Transition } from '@headlessui/react';
import ReactPlayer from 'react-player/lazy';
import { Icon } from '@iconify/react';
import Close from '@iconify/icons-mdi/close';
import Expand from '@iconify/icons-mdi/arrow-expand-all';

import VideoPlayer from '~/components/modules/video-player';

import trackEvent from '~/hooks/useEventTracker';

import PlaceholderDocument from '~/images/inline/placeholder-document.svg';
import PlaceholderAudioFile from '~/images/inline/placeholder-audio-file.svg';

const ItemType = Object.freeze({
	DEFAULT: 'default',
	AUDIO: 'audio',
	VIDEO: 'video'
});

const Thumbnail = ({
	item,
	preventPreview,
	mode,
	className,
	thumbnailClassName
}) => {
	const [thumbnail] =
		item.Thumbnail && item.Thumbnail.localFiles
			? item.Thumbnail.localFiles
			: '';
	let [previewIsOpen, setPreviewIsOpen] = useState(false);

	const closeModal = () => {
		setPreviewIsOpen(false);
	};

	const openModal = () => {
		if (!preventPreview) {
			setPreviewIsOpen(true);
			trackEvent(
				'Engagement',
				'Open Preview',
				`${item.Name} ${item.Item_Type[0].data.Name}`
			);
		}
	};

	const getItemType = item => {
		if (!item.File_Type || item.File_Type.length === 0) return ItemType.DEFAULT;
		switch (item.File_Type[0].data.Name) {
			case 'MP4':
				return ItemType.VIDEO;
			case 'MP3':
				return ItemType.AUDIO;
			default:
				return ItemType.DEFAULT;
		}
	};

	return (
		<div className={className ?? ''}>
			{thumbnail && thumbnail.childImageSharp ? (
				<>
					<button
						type="button"
						onClick={openModal}
						className={`
							${preventPreview ? 'cursor-default' : ''}
							group relative w-full flex text-center mb-2 lg:mb-0 items-start
						`}
					>
						<div
							className={`
								border border-gray-400 w-full
								${mode !== 'orderMode' ? 'h-24 overflow-hidden' : ''}
								${thumbnailClassName ?? ''}
							`}
						>
							{getItemType(item) === ItemType.AUDIO ? (
								<PlaceholderAudioFile
									className="w-full"
									aria-label="Placeholder"
									aria-hidden="true"
									role="img"
								/>
							) : (
								<GatsbyImage
									image={thumbnail.childImageSharp.gatsbyImageData}
									alt={item.Name}
									className={`
										${mode !== 'orderMode' ? 'h-24' : 'h-auto object-contain'}
										w-full object-top
									`}
								/>
							)}
						</div>
						<span
							className={`
								${preventPreview ? 'hidden' : ''}
								absolute top-0 right-0 z-10 md:translate-x-1/2 md:-translate-y-1/2 bg-card-product-thumbnail-expand-background-default md:bg-white md:bg-opacity-40 text-white md:text-card-product-thumbnail-expand-color p-1 transition-colors group-hover:text-white group-hover:bg-card-product-thumbnail-expand-background-hover group-focus:text-white group-focus:bg-card-product-thumbnail-expand-background-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-card-product-thumbnail-expand-background-hover
							`}
							aria-label="Show product preview"
						>
							<Icon
								icon={Expand}
								height="1rem"
								width="1rem"
								className="w-4 h-4"
							/>
						</span>
					</button>
					<Transition appear show={previewIsOpen} as={Fragment}>
						<Dialog
							as="div"
							className="fixed inset-0 z-50 overflow-y-auto"
							onClose={closeModal}
						>
							<div className="min-h-screen px-4 text-center">
								<Transition.Child
									as={Fragment}
									enter="ease-out duration-300"
									enterFrom="opacity-0"
									enterTo="opacity-100"
									leave="ease-in duration-200"
									leaveFrom="opacity-100"
									leaveTo="opacity-0"
								>
									<Dialog.Overlay className="fixed inset-0 bg-black/40" />
								</Transition.Child>

								{/* This element is to trick the browser into centering the modal contents. */}
								<span
									className="inline-block h-screen align-middle"
									aria-hidden="true"
								>
									&#8203;
								</span>
								<Transition.Child
									as={Fragment}
									enter="ease-out duration-300"
									enterFrom="opacity-0 scale-95"
									enterTo="opacity-100 scale-100"
									leave="ease-in duration-200"
									leaveFrom="opacity-100 scale-100"
									leaveTo="opacity-0 scale-95"
								>
									<div className="inline-block relative w-full md:w-auto md:min-w-[24rem] max-w-sm max-h-[90vh] px-2 pb-2 overflow-hidden align-middle transition-all transform bg-white shadow-xl">
										<div className="text-right">
											<button
												type="button"
												className="inline-flex items-center p-2 font-semibold text-xs uppercase text-card-product-thumbnail-close-color-default transition-colors hover:text-card-product-thumbnail-close-color-hover focus:text-card-product-thumbnail-close-color-hover focus:outline-none"
												onClick={closeModal}
											>
												<span>Close</span>
												<Icon
													icon={Close}
													height="1rem"
													width="1rem"
													className="w-4 h-4 ml-1"
												/>
											</button>
										</div>

										{getItemType(item) === ItemType.AUDIO && (
											<div>
												<ReactPlayer
													className="border-0 aspect-[7/1]"
													url={item.Availability[0].data.Payload}
													width="100%"
													height="100%"
													controls={true}
												/>
											</div>
										)}

										{getItemType(item) === ItemType.VIDEO && (
											<div>
												<VideoPlayer
													url={item.Availability[0].data.Payload}
													placeholder={thumbnail.childImageSharp.fluid?.srcWebp}
													playerOptions={{
														controls: true,
														playsinline: true
													}}
												/>
											</div>
										)}

										{getItemType(item) === ItemType.DEFAULT && (
											<GatsbyImage
												image={thumbnail.childImageSharp.gatsbyImageData}
												alt={item.Name}
												imgClassName="!max-h-[80vh] !w-auto !mx-auto !max-w-full"
											/>
										)}
									</div>
								</Transition.Child>
							</div>
						</Dialog>
					</Transition>
				</>
			) : (
				<PlaceholderDocument
					className="w-full"
					aria-label="Placeholder"
					aria-hidden="true"
					role="img"
				/>
			)}
		</div>
	);
};

export default Thumbnail;
